import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useSession } from "next-auth/react";

/**
 * Replaces `middleware.ts`, which used `withAuth` to protect `/checkout`,
 * `/dashboard/*` and `/admin/*`.
 *
 * The matcher and the rules are identical:
 *   - no session            → `/login?callbackUrl=<current path>`
 *   - session, not an ADMIN → `/` (for `/admin` only)
 *
 * The one difference is that the check runs in the browser instead of on the
 * edge, so there is a brief loading state while the session is fetched.
 */
function Loading() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-amazon-orange" />
      <span className="sr-only">Checking your session</span>
    </div>
  );
}

export function RequireAuth() {
  const { status } = useSession();
  const location = useLocation();

  if (status === "loading") return <Loading />;
  if (status === "unauthenticated") {
    const callbackUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`} replace />;
  }

  return <Outlet />;
}

export function RequireAdmin() {
  const { status, data: session } = useSession();
  const location = useLocation();

  if (status === "loading") return <Loading />;
  if (status === "unauthenticated") {
    const callbackUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`} replace />;
  }
  // Signed in, but not an admin: `middleware.ts` bounced these to the homepage.
  if (session?.user?.role !== "ADMIN") return <Navigate to="/" replace />;

  return <Outlet />;
}

export function RequireAdmin1() {
  const { status, data: session } = useSession();
  const location = useLocation();

  if (status === "loading") return <Loading />;
  if (status === "unauthenticated") {
    const callbackUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`} replace />;
  }
  if (session?.user?.role !== "ADMIN1" && session?.user?.role !== "ADMIN") return <Navigate to="/" replace />;

  return <Outlet />;
}

export function RequireVendor() {
  const { status, data: session } = useSession();
  const location = useLocation();

  if (status === "loading") return <Loading />;
  if (status === "unauthenticated") {
    const callbackUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`} replace />;
  }
  if (session?.user?.role !== "VENDOR" && session?.user?.role !== "ADMIN") {
    return <Navigate to="/sell" replace />;
  }

  return <Outlet />;
}

export function RequireGuest({ children }: { children: ReactNode }) {
  const { status } = useSession();
  if (status === "loading") return <Loading />;
  return <>{children}</>;
}